import { Fragment, type ReactNode } from "react";
import { cx } from "@/shared/lib/cx";
import { IconChip, type ChipTone, type IconName } from "../Icon";

export function Stat({ label, value, hint, icon, tone, compact }: {
  label: string; value: ReactNode; hint?: ReactNode; icon?: IconName;
  tone?: ChipTone; compact?: boolean;
}) {
  return (
    <div className={cx("stat", compact && "stat-compact")}>
      <div className="stat-head">
        <span className="stat-label">{label}</span>
        {icon && <IconChip name={icon} tone={tone} size="sm" />}
      </div>
      <span className="stat-value">{typeof value === "number" ? value.toLocaleString("vi-VN") : value}</span>
      {hint && <span className="stat-hint">{hint}</span>}
    </div>
  );
}

/** Horizontal progress bar with its value (accuracy, completion…). `value` in 0–1. */
export function Meter({ value, label, tone }: { value: number; label?: ReactNode; tone?: "success" | "warning" | "danger" }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <span className="meter">
      <span className="meter-track" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}>
        <span className={cx("meter-fill", tone && `is-${tone}`)} style={{ width: `${pct}%` }} />
      </span>
      <span className="meter-value">{label ?? `${Math.round(pct)}%`}</span>
    </span>
  );
}

/** Term / value pairs (profile facts, settings summaries). */
export function KeyValue({ items }: { items: { label: ReactNode; value: ReactNode }[] }) {
  return (
    <dl className="kv">
      {items.map((it, i) => (
        <Fragment key={i}>
          <dt>{it.label}</dt>
          <dd>{it.value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

const AVATAR_HUES = [174, 199, 221, 262, 291, 330, 14, 32, 152];

/** Round avatar: the picture when there is one, otherwise initials on a stable colour. */
export function Avatar({ name, src, size = "md" }: { name: string; src?: string; size?: "sm" | "md" | "lg" }) {
  const initials = name.split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join("") || "?";
  let hash = 0;
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  const hue = AVATAR_HUES[hash % AVATAR_HUES.length];
  return (
    <span className={cx("avatar", size !== "md" && `avatar-${size}`)} style={{ ["--avatar-bg" as string]: `hsl(${hue} 55% 42%)` }} aria-hidden>
      {src ? <img src={src} alt="" referrerPolicy="no-referrer" loading="lazy" /> : initials}
    </span>
  );
}
